// AIChatModal - AI 対話でピースマップを生成（spec.md §5.8.5）
// 既存 AIGenerationModal の単発生成を対話化に置換。
// 左: チャット履歴 / 右: 3案プレビュー。API はスタブ（features/builder/api.ts）。

import { useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Backdrop, Fade } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { RootState } from "app/store";
import { RestrictedFeatureSource } from "app/types";
import I18n from "common/i18n";
import { useScale, usePlaySound, btnDelay } from "common/utils";
import { actions } from "features/builder/slice";
import { aiGenerate, aiRefine } from "features/builder/api";
import { useBuilderContext } from "features/builder/context/BuilderContext";
import { usePlanLimits, useEnforcePlanLimit } from "features/subscription/hooks";
import { Difficulty, PieceAiRole, GeneratedMapData } from "features/builder/types";

const MODAL_WIDTH = 1180;
const MODAL_HEIGHT = 800;

type ChatMessage = {
  role: PieceAiRole;
  content: string;
};

export const AIChatModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch();
  const { triggerReload } = useBuilderContext();
  const planLimits = usePlanLimits();
  const enforcePlanLimit = useEnforcePlanLimit();

  const size = useSelector((state: RootState) => state.builder.size);
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const active = useSelector((state: RootState) => state.user.active);

  const [prompt, setPrompt] = useState("");
  const [difficulty, setDifficulty] = useState<Difficulty>(Difficulty.MEDIUM);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [variations, setVariations] = useState<GeneratedMapData[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // TODO(piece-builder): 専用 FeatureKey.AI_MAP_GENERATE 未整備のため
  // 暫定で aiChatDaily クォータを流用（api-contract.md 準拠、fail-open）。
  const quota = planLimits.aiChatDaily;

  const handleClose = () => {
    if (isGenerating) return;
    play();
    btnDelay(() => onClose());
  };

  const runGenerate = async (text: string) => {
    setIsGenerating(true);
    setMessages((prev) => [...prev, { role: PieceAiRole.USER, content: text }]);
    try {
      let result;
      if (sessionId) {
        try {
          result = await aiRefine({
            uid: active.uid,
            session_id: sessionId,
            prompt: text,
            difficulty,
            size: { x: size.x, z: size.z },
          });
        } catch (err) {
          // 410 Gone: thread 失効 → 新規 session に自動フォールバック
          if ((err as { status?: number }).status === 410) {
            toast(I18n.t("MSG_BUILDER_AI_SESSION_EXPIRED"));
            result = await aiGenerate({
              uid: active.uid,
              piece_id: pieceId,
              prompt: text,
              difficulty,
              size: { x: size.x, z: size.z },
            });
          } else {
            throw err;
          }
        }
      } else {
        result = await aiGenerate({
          uid: active.uid,
          piece_id: pieceId,
          prompt: text,
          difficulty,
          size: { x: size.x, z: size.z },
        });
      }
      setSessionId(result.session_id);
      setVariations(result.variations);
      setMessages((prev) => [
        ...prev,
        { role: PieceAiRole.ASSISTANT, content: I18n.t("MSG_BUILDER_AI_GREETING") },
      ]);
    } catch (err) {
      console.error("AI generate failed:", err);
      toast(I18n.t("MSG_BUILDER_AI_GENERATION_FAILED"));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSend = () => {
    if (isGenerating || !prompt.trim()) return;
    if (!quota.unlimited && quota.remaining <= 0) {
      enforcePlanLimit(RestrictedFeatureSource.AI_CHAT_LIMIT);
      return;
    }
    play();
    const text = prompt.trim();
    setPrompt("");
    btnDelay(() => runGenerate(text));
  };

  const handleUseVariation = (variation: GeneratedMapData) => () => {
    play();
    btnDelay(() => {
      dispatch(actions.applyGeneratedMap(variation));
      triggerReload();
      onClose();
    });
  };

  const difficultyOptions = [
    { value: Difficulty.EASY, labelKey: "MSG_BUILDER_DIFFICULTY_EASY", stars: "⭐" },
    { value: Difficulty.MEDIUM, labelKey: "MSG_BUILDER_DIFFICULTY_MEDIUM", stars: "⭐⭐" },
    { value: Difficulty.HARD, labelKey: "MSG_BUILDER_DIFFICULTY_HARD", stars: "⭐⭐⭐" },
  ];

  return (
    <Backdrop
      open={open}
      sx={{ zIndex: 100000, backgroundColor: "transparent", backdropFilter: "blur(4px)" }}
    >
      <Fade in={open}>
        <div
          className="flex-col-el flex-center w-full h-full bg-textcolor-black/50"
          onClick={handleClose}
        >
          <div
            className="flex-col-el origin-center"
            style={{ transform: `scale(${scale})` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex-col-el bg-white rounded-2xl border-4 border-white shadow-2xl overflow-hidden"
              style={{ width: MODAL_WIDTH, height: MODAL_HEIGHT }}
            >
              {/* ヘッダー */}
              <div className="flex-row-el items-center justify-between gap-3 px-6 py-4 bg-emerald-500">
                <div className="flex-row-el items-center gap-3">
                  <span className="flex-col-el flex-center type-title text-white">
                    <SmartToyIcon fontSize="inherit" />
                  </span>
                  <div className="flex-col-el items-start">
                    <span className="type-title text-white">
                      {I18n.t("MSG_BUILDER_AI_CHAT_TITLE")}
                    </span>
                    <span className="type-sm text-white">
                      {I18n.t("MSG_BUILDER_AI_CHAT_SUBTITLE")}
                    </span>
                  </div>
                </div>
                <div className="flex-row-el items-center gap-3">
                  <span className="type-sm text-white">
                    {I18n.t("MSG_BUILDER_AI_QUOTA", {
                      remaining: quota.unlimited ? "∞" : quota.remaining,
                      limit: quota.unlimited ? "∞" : quota.limit,
                    })}
                  </span>
                  <button
                    onClick={handleClose}
                    className="flex-col-el flex-center type-title text-white opacity-button type-inherit-font"
                  >
                    <CloseIcon fontSize="inherit" />
                  </button>
                </div>
              </div>

              {/* ボディ */}
              <div className="flex-row-el flex-1 overflow-hidden">
                {/* 左: チャット履歴 + 入力 */}
                <div className="flex-col-el border-r-2 border-gray-200" style={{ width: 480 }}>
                  <div className="flex-col-el items-stretch gap-2 p-4 flex-1 overflow-y-auto">
                    {messages.length === 0 && (
                      <span className="type-sm text-gray-500 text-center">
                        {I18n.t("MSG_BUILDER_AI_GREETING")}
                      </span>
                    )}
                    {messages.map((msg, i) => (
                      <div
                        key={i}
                        className={`flex-col-el max-w-[80%] rounded-xl p-3 ${
                          msg.role === PieceAiRole.USER
                            ? "self-end bg-emerald-100"
                            : "self-start bg-gray-100"
                        }`}
                      >
                        <span className="type-sm text-textcolor-black">{msg.content}</span>
                      </div>
                    ))}
                    {isGenerating && (
                      <div className="flex-col-el items-center gap-2 py-4">
                        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>

                  {/* 難易度 + 入力 */}
                  <div className="flex-col-el items-stretch gap-2 p-4 border-t-2 border-gray-200">
                    <div className="flex-row-el gap-2">
                      {difficultyOptions.map((opt) => (
                        <div key={opt.value} className="flex-1 upsilon">
                          <button
                            onClick={() => {
                              play();
                              setDifficulty(opt.value);
                            }}
                            className={`btn type-inherit-font type-sm w-full ${
                              difficulty === opt.value ? "btn-primary" : "btn-blue"
                            }`}
                            style={{ margin: 0 }}
                          >
                            {opt.stars} {I18n.t(opt.labelKey)}
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex-row-el items-center gap-2">
                      <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder={I18n.t("MSG_BUILDER_AI_INPUT_PLACEHOLDER")}
                        maxLength={100}
                        rows={2}
                        className="input-modal type-sm text-textcolor-black flex-1 resize-none"
                      />
                      <div className="upsilon">
                        <button
                          onClick={handleSend}
                          disabled={isGenerating}
                          className="btn btn-primary type-inherit-font type-sm"
                          style={{ margin: 0 }}
                        >
                          {I18n.t("MSG_BUILDER_AI_SEND_BTN")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 右: 3案プレビュー */}
                <div className="flex-col-el items-stretch gap-3 p-4 flex-1 overflow-y-auto">
                  {variations.length === 0 ? (
                    <div className="flex-col-el flex-center flex-1">
                      <span className="type-sm text-gray-400">
                        {I18n.t("MSG_BUILDER_AI_GREETING")}
                      </span>
                    </div>
                  ) : (
                    variations.map((variation, i) => (
                      <div
                        key={i}
                        className="flex-col-el items-stretch gap-2 rounded-xl border-2 border-gray-200 p-3"
                      >
                        <span className="type-sm text-textcolor-black">
                          {I18n.t("MSG_BUILDER_AI_PLAN_LABEL", { n: i + 1 })}
                        </span>
                        <div className="flex-row-el items-center gap-2 rounded-lg bg-gray-50 p-2">
                          <span className="type-title">🗺️</span>
                          <span className="type-sm text-gray-500">
                            {`${variation.tiles.length} tiles`}
                          </span>
                        </div>
                        <div className="upsilon">
                          <button
                            onClick={handleUseVariation(variation)}
                            className="btn btn-primary type-inherit-font type-sm w-full"
                            style={{ margin: 0 }}
                          >
                            {I18n.t("MSG_BUILDER_AI_USE_THIS")}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fade>
    </Backdrop>
  );
};
